import React from "react";
import { ToolbarMoreButton } from "./FluentToolbarMoreButton";
import {
  Accessibility24Filled,
  BookQuestionMark24Filled,
  Camera24Filled,
  Chat24Filled,
  ClosedCaptionFilled,
  ClosedCaptionOffFilled,
  DocumentBulletList24Filled,
  DocumentArrowUpFilled,
  DrawImageFilled,
  Emoji24Filled,
  FlagFilled,
  HandLeftFilled,
  HandRightOffFilled,
  ImageAltTextFilled,
  ImageCopy24Filled,
  Pen24Filled,
  People24Filled,
  QuestionCircle24Filled,
  Settings24Filled,
  ShareScreenStart24Filled,
  ShareScreenStop24Filled,
  Translate24Regular,
  VideoClipOptimize24Filled,
  BugFilled
} from "@fluentui/react-icons"; // Example icons
import PropTypes from "prop-types";
import configs from "../../utils/configs";
import { useSettingsContext } from "../room/SettingsContext";
import { FormattedMessage } from "react-intl";
import { useToolbarContext } from "../contexts/ToolbarContext";
import { useSocialContext } from "../contexts/SocialContext";
import { useUploadFormContext } from "../contexts/UploadFormContext";
import { useShare } from "../room/useShare";
import configManager from "../../singleton/ConfigManager";
import { Badge, InfoLabel, makeStyles } from "@fluentui/react-components";
import { useSidebarContext } from "../SidebarContext";
import { usePresence, useToggleRaisedHand } from "./FluentToolbarRaiseHandButtonContainer";
import { ReportBugDialog } from "../room/ReportBugDialog";
import { SignInMessages } from "../auth/SignInModal";
import { useTranscription } from "../transcription/TranscriptionContext";
import { getLanguageLabel } from "../transcription/transcription-language-utils";
import { useRoomPersistence } from "../room-persistence/RoomPersistenceContext";
import { SummaryGenerationDialog } from "../summary/SummaryGenerationDialog";
import { getSummaryMenuItemState } from "../summary/summary-menu-items";
import {
  PARTICIPANT_LAYOUT_FORCED_STATE,
  createParticipantLayoutMoreMenuGroup,
  filterParticipantLayoutActiveMenuGroups,
  filterToolbarMoreMenuGroups
} from "./toolbar-more-button-items";
import { useParticipantLayoutActionAvailability } from "./participant-layout/participant-layout-action-availability";
import { useUnavailableToolbarAction } from "./useUnavailableToolbarAction";
import { ReactComponent as ShareScreenOffIcon } from "../icons/ShareScreenOff.svg";
import { isScreenShareEnabled } from "../../utils/media-sharing-config-utils";
import { shouldShowScreenShareAction } from "./toolbar-screen-share-availability";
import { isWebRtcDisabled } from "../../utils/room-entry-admission";

const useStyles = makeStyles({
  active: {
    backgroundColor: "var(--primaryColorBackground)"
  },
  infoLabel: {
    display: "inline-flex",
    width: "16px",
    height: "16px",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: "0",
    color: "var(--color-text-primary)"
  },
  infoLabelText: {
    display: "none"
  },
  infoButton: {
    width: "16px",
    minWidth: "16px",
    height: "16px",
    padding: "0",
    margin: "0",
    color: "var(--color-text-primary)",
    lineHeight: "0",
    verticalAlign: "middle",
    "& svg": {
      width: "16px",
      height: "16px",
      color: "var(--color-text-primary)",
      fill: "var(--color-text-primary)"
    }
  },
  infoLabelWrapper: {
    display: "inline-flex",
    width: "16px",
    height: "16px",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    lineHeight: "0"
  }
});

function MoreMenuInfoLabel({ info }) {
  const classes = useStyles();
  const stopMenuItemEvent = event => {
    event.stopPropagation();
  };

  return (
    <span
      className={classes.infoLabelWrapper}
      onClick={stopMenuItemEvent}
      onKeyDown={stopMenuItemEvent}
      onPointerDown={stopMenuItemEvent}
    >
      <InfoLabel
        className={classes.infoLabel}
        size="small"
        info={info}
        label={{ className: classes.infoLabelText }}
        infoButton={{
          "aria-label": info,
          className: classes.infoButton
        }}
      >
        {""}
      </InfoLabel>
    </span>
  );
}

MoreMenuInfoLabel.propTypes = {
  info: PropTypes.node.isRequired
};

function renderSummaryMenuLabel(state) {
  if (state?.active) {
    return <FormattedMessage id="toolbar.ai-summary.generating" defaultMessage="Generating Summary" />;
  }

  return <FormattedMessage id="toolbar.ai-summary.generate" defaultMessage="Generate Summary" />;
}

export function FluentToolbarMoreButtonContainer({
  styles,
  scene,
  hubChannel,
  people = [],
  mediaSearchStore,
  initialPresence,
  store,
  toggleStreamerMode,
  participantLayoutEnabled = false,
  participantLayoutForced = false,
  onParticipantLayoutToggle,
  participantLayoutRoomEnabled,
  participantLayoutForcedState,
  onParticipantLayoutManagementCheckedValueChange,
  performConditionalSignIn
}) {
  const { onOpenChange: onSettingsOpenChange } = useSettingsContext();
  const { onEmojisOpenChange, canSpawnEmojis } = useToolbarContext();
  const { privateUnreadMessages } = useSocialContext();
  const { toggleParticipants, toggleChat } = useSocialContext();
  const { setFileUploadOpen, onDigitalArtChange, onDigitalTwinArtChange } = useToolbarContext();
  const { onClear } = useUploadFormContext();
  const { participantLayoutActive, canManageFiles, canUploadFiles } = useParticipantLayoutActionAvailability();

  const { canShareScreen, toggleShareScreen, isSharingScreen, screenShareActionState } = useShare(scene, hubChannel);
  const screenShareUnavailable = screenShareActionState.blocked
    ? screenShareActionState.unavailableReason || "Screen sharing is unavailable"
    : null;
  const notifyScreenShareUnavailable = useUnavailableToolbarAction(screenShareUnavailable);
  const classes = useStyles();
  const {
    hasTranscriptionAgent,
    isCCEnabled,
    toggleCC,
    translationEnabled,
    agentAllowsTranslation,
    targetLanguage,
    setTargetLanguage,
    toggleTranslation,
    availableLanguages
  } = useTranscription();
  const { generateSummary, hasSummaryAgent, isGeneratingSummary, isSummaryConfigured, canGenerateSummary, summaryDataSources } =
    useRoomPersistence();

  // State for report bug dialog
  const [reportBugOpen, setReportBugOpen] = React.useState(false);
  const [summaryDialogOpen, setSummaryDialogOpen] = React.useState(false);

  const communicationEnabled = !isWebRtcDisabled();
  const canSpawnCamera =
    communicationEnabled && !!configManager.config.spawnCamera.enabled && hubChannel.can("spawn_camera");

  const textChatEnabled = communicationEnabled && !!configManager.config.textChat?.enabled;
  const participantLayoutMenuEnabled = !!configManager.config.participantLayout?.enabled;
  const participantLayoutConfigForceEnabled = !!configManager.config.participantLayout?.forceEnabled;
  const effectiveParticipantLayoutRoomEnabled =
    participantLayoutRoomEnabled === undefined ? participantLayoutMenuEnabled : participantLayoutRoomEnabled;
  const effectiveParticipantLayoutForcedState =
    participantLayoutForcedState ||
    (participantLayoutForced ? PARTICIPANT_LAYOUT_FORCED_STATE.OPEN : PARTICIPANT_LAYOUT_FORCED_STATE.NONE);
  const canManageParticipantLayout =
    participantLayoutMenuEnabled &&
    !participantLayoutConfigForceEnabled &&
    !!onParticipantLayoutManagementCheckedValueChange &&
    hubChannel.can("update_hub");
  const canSpawnAndMoveMedia = hubChannel.can("spawn_and_move_media");
  const screenShareConfigEnabled = communicationEnabled && isScreenShareEnabled(configManager.config);
  const canSpawnObjects = !!configManager.config.spawnAndMoveMedia.enabled && canSpawnAndMoveMedia;
  const canShareScreens = screenShareConfigEnabled && canSpawnAndMoveMedia;
  const showScreenShareMenuItem = shouldShowScreenShareAction({
    screenShareConfigEnabled,
    canSpawnAndMoveMedia,
    canShareScreen,
    isSharingScreen,
    screenShareHidden: screenShareActionState.hidden
  });
  const emojiEnabled = !!configManager.config.spawnEmoji.enabled && hubChannel.can("spawn_emoji");
  const summaryMenuItemState = getSummaryMenuItemState({
    isConfigured: isSummaryConfigured,
    hasSummaryAgent,
    canManage: canGenerateSummary,
    isGenerating: isGeneratingSummary
  });

  const { setSidebar } = useSidebarContext();

  const handleAvatarClick = () => {
    mediaSearchStore.sourceNavigate("avatars");
    setSidebar("avatar");
  };

  const canSpawnDrawn = hubChannel.can("spawn_drawing");

  const onSummaryClick = () => {
    if (!summaryMenuItemState || summaryMenuItemState.disabled) {
      return;
    }

    setSummaryDialogOpen(true);
  };

  const onGenerateSummaryFromDialog = options =>
    generateSummary(options).catch(error => {
      console.warn("[RoomPersistence] Summary generation failed", error);
      throw error;
    });

  const fileUploadOpenMenu = () => {
    if (!canUploadFiles) {
      return;
    }

    onClear(); // Clear any existing edit state
    onDigitalTwinArtChange(false);
    onDigitalArtChange(false);
    setFileUploadOpen(true);
  };

  const openDigitalArt = () => {
    if (!canUploadFiles) {
      return;
    }

    onClear(); // Clear any existing edit state
    onDigitalArtChange(true);
    onDigitalTwinArtChange(false);
    setFileUploadOpen(true);
  };

  const openDigitalTwinArt = () => {
    if (!canUploadFiles) {
      return;
    }

    onClear(); // Clear any existing edit state
    onDigitalArtChange(false);
    onDigitalTwinArtChange(true);
    setFileUploadOpen(true);
  };

  const presence = usePresence(scene, initialPresence);
  const onToggleHandRaised = useToggleRaisedHand(presence, hubChannel, store);

  // Handle report bug dialog
  const handleReportBugOpen = () => {
    performConditionalSignIn(
      () => hubChannel.signedIn,
      () => setReportBugOpen(true),
      SignInMessages.reportBug
    );
  };

  const handleReportBugClose = () => {
    setReportBugOpen(false);
  };

  const handleReportBugConfirm = async () => {
    // The ReportBugDialog now handles the API call and closing internally
    // This function is kept for backward compatibility and any additional logic
    // The dialog will close itself on successful submission
  };

  const artworkSubMenuItems = [
    {
      id: "file",
      label: <FormattedMessage id="upload.file" defaultMessage="File" />,
      icon: <DocumentArrowUpFilled />, // Replace with appropriate icon
      onClick: fileUploadOpenMenu
    },
    {
      id: "digitalArt",
      label: <FormattedMessage id="upload.digitalArt" defaultMessage="Digital Art" />,
      icon: <ImageAltTextFilled />, // Replace with appropriate icon
      onClick: openDigitalArt
    },
    {
      id: "digitalTwinArt",
      label: <FormattedMessage id="upload.digitalTwinArt" defaultMessage="Digital Twin Art" />,
      icon: <DrawImageFilled />, // Replace with appropriate icon
      onClick: openDigitalTwinArt
    }
  ];

  const startTour = () => {
    scene.systems.tips.resetTips();
  };

  const menuItems = [
    {
      title: <FormattedMessage id="interaction-group" defaultMessage="Interaction" />,
      items: [
        canSpawnDrawn
          ? {
              id: "pen",
              label: <FormattedMessage id="more-menu.pen" defaultMessage="Pen" />,
              icon: <Pen24Filled />,
              onClick: () => scene.emit("penButtonPressed")
            }
          : undefined,
        canSpawnCamera
          ? {
              id: "camera",
              label: <FormattedMessage id="more-menu.camera" defaultMessage="Selfie" />,
              icon: <Camera24Filled />,
              onClick: () => scene.emit("action_toggle_camera")
            }
          : undefined,
        canSpawnEmojis && emojiEnabled
          ? {
              id: "emojis",
              label: <FormattedMessage id="reaction-popover.title" defaultMessage="Emojis" />,
              icon: <Emoji24Filled />,
              onClick: () => onEmojisOpenChange(true)
            }
          : undefined,
        showScreenShareMenuItem && canShareScreens
          ? {
              id: "shareScreen",
              label: isSharingScreen ? (
                <FormattedMessage id="room-settings-sidebar.stop-screen-sharing" defaultMessage="Stop Screen Sharing" />
              ) : (
                <FormattedMessage id="room-settings-sidebar.share-screen" defaultMessage="Share Screen" />
              ),
              icon: isSharingScreen ? (
                <ShareScreenStop24Filled />
              ) : screenShareUnavailable ? (
                <ShareScreenOffIcon style={{ color: "var(--color-danger)" }} />
              ) : (
                <ShareScreenStart24Filled />
              ),
              onClick: screenShareUnavailable ? notifyScreenShareUnavailable : toggleShareScreen,
              unavailable: !!screenShareUnavailable,
              secondaryContent: screenShareUnavailable ? (
                <MoreMenuInfoLabel info={screenShareUnavailable} />
              ) : undefined,
              className: isSharingScreen ? classes.active : undefined
            }
          : undefined,
        // Add Raised Hand button here if needed
        canSpawnEmojis && emojiEnabled
          ? {
              id: "Raised Hand",
              label: presence?.hand_raised ? (
                <FormattedMessage id="toolbar.lower-hand" defaultMessage="Lower Hand" />
              ) : (
                <FormattedMessage id="toolbar.raise-hand" defaultMessage="Raise Hand" />
              ),
              icon: presence?.hand_raised ? <HandRightOffFilled /> : <HandLeftFilled />,
              onClick: onToggleHandRaised,
              className: presence?.hand_raised ? classes.active : undefined
            }
          : undefined,
        hasTranscriptionAgent
          ? {
              id: "captions",
              label: isCCEnabled ? (
                <FormattedMessage id="toolbar.cc-on" defaultMessage="Captions on" />
              ) : (
                <FormattedMessage id="toolbar.cc-off" defaultMessage="Captions off" />
              ),
              icon: isCCEnabled ? <ClosedCaptionFilled /> : <ClosedCaptionOffFilled />,
              className: isCCEnabled ? classes.active : undefined,
              onClick: !agentAllowsTranslation ? toggleCC : undefined,
              submenuCheckedValues: agentAllowsTranslation
                ? {
                    "captions-translation-enabled": translationEnabled ? ["enabled"] : [],
                    "captions-target-language": [targetLanguage || ""]
                  }
                : undefined,
              submenu: agentAllowsTranslation
                ? [
                    {
                      id: "captions-toggle",
                      label: isCCEnabled ? (
                        <FormattedMessage id="toolbar.cc-disable" defaultMessage="Disable captions" />
                      ) : (
                        <FormattedMessage id="toolbar.cc-enable" defaultMessage="Enable captions" />
                      ),
                      icon: isCCEnabled ? <ClosedCaptionFilled /> : <ClosedCaptionOffFilled />,
                      onClick: toggleCC,
                      closeMenuOnClick: false
                    },
                    {
                      id: "captions-translation-toggle",
                      type: "switch",
                      name: "captions-translation-enabled",
                      value: "enabled",
                      label: <FormattedMessage id="toolbar.cc-translate" defaultMessage="Translate captions" />,
                      icon: <Translate24Regular />,
                      onClick: toggleTranslation,
                      closeMenuOnClick: false
                    },
                    ...(translationEnabled
                      ? availableLanguages.map(languageCode => ({
                          id: `captions-target-language-${languageCode}`,
                          type: "radio",
                          name: "captions-target-language",
                          value: languageCode,
                          label: getLanguageLabel(languageCode),
                          onClick: () => setTargetLanguage(languageCode),
                          closeMenuOnClick: false
                        }))
                      : [])
                  ]
                : undefined
            }
          : undefined,
        canSpawnObjects && {
          id: "Lobby Cast",
          label: <FormattedMessage id="lobby-cast" defaultMessage="Lobby Cast" />,
          icon: <VideoClipOptimize24Filled />,
          onClick: toggleStreamerMode
        }
      ].filter(Boolean) // Remove undefined items
    },
    {
      title: <FormattedMessage id="collaboration-group" defaultMessage="Collaboration" />,
      items: [
        textChatEnabled && {
          id: "chat",
          label: <FormattedMessage id="chat-toolbar-button" defaultMessage="Chat" />,
          icon: <Chat24Filled />,
          onClick: toggleChat
        },
        summaryMenuItemState && {
          id: "summary",
          label: renderSummaryMenuLabel(summaryMenuItemState),
          icon: <DocumentBulletList24Filled />,
          onClick: onSummaryClick,
          disabled: summaryMenuItemState.disabled,
          className: summaryMenuItemState.active ? classes.active : undefined
        },
        {
          id: "participants",
          label: (
            <>
              <FormattedMessage
                id="people-sidebar.title"
                defaultMessage="Participants ({numPeople})"
                values={{ numPeople: people.length }}
              />
              {privateUnreadMessages != false && (
                <Badge size="small" style={{ position: "absolute", right: "var(--spacingVerticalSNudge)" }} />
              )}
            </>
          ),
          icon: <People24Filled />,
          onClick: toggleParticipants
        }
      ].filter(Boolean)
    },
    createParticipantLayoutMoreMenuGroup({
      enabled: participantLayoutEnabled,
      forced: participantLayoutForced,
      menuEnabled: participantLayoutMenuEnabled && effectiveParticipantLayoutRoomEnabled,
      onToggle: onParticipantLayoutToggle,
      canManage: canManageParticipantLayout,
      roomLayoutEnabled: effectiveParticipantLayoutRoomEnabled,
      forcedState: effectiveParticipantLayoutForcedState,
      onManagementCheckedValueChange: onParticipantLayoutManagementCheckedValueChange
    }),
    {
      title: <FormattedMessage id="resources-group" defaultMessage="Resources" />,
      items: [
        canSpawnObjects && {
          id: "addAvatar",
          label: <FormattedMessage id="more-menu.avatar" defaultMessage="Avatars" />,
          icon: <Accessibility24Filled />,
          onClick: handleAvatarClick
        },
        canSpawnObjects &&
          canUploadFiles && {
            id: "upload",
            label: <FormattedMessage id="place-popover.item-type.upload" defaultMessage="Upload" />,
            icon: <DocumentArrowUpFilled />,
            submenu: artworkSubMenuItems
          },
        canSpawnObjects &&
          canManageFiles && {
            id: "assets",
            label: <FormattedMessage id="more-menu.assets" defaultMessage="Assets" />,
            icon: <ImageCopy24Filled />,
            onClick: () =>
              performConditionalSignIn(
                () => hubChannel.signedIn,
                () => setSidebar("assets")
              )
          }
      ].filter(Boolean)
    },
    {
      title: <FormattedMessage id="metaverse-group" defaultMessage="Metaverse" />,
      items: [
        {
          id: "settings",
          label: <FormattedMessage id="settings" defaultMessage="Settings" />, // Wrapped in FormattedMessage for consistency
          icon: <Settings24Filled />,
          onClick: () => onSettingsOpenChange(true)
        },
        {
          id: "help",
          label: <FormattedMessage id="help" defaultMessage="Help" />, // Wrapped in FormattedMessage for consistency
          icon: <QuestionCircle24Filled />,
          positioning: "after-bottom",
          submenu: [
            {
              id: "documentation",
              label: <FormattedMessage id="help.documentation" defaultMessage="Documentation" />,
              href: configs.DOCS_URL,
              target: "_blank",
              icon: <BookQuestionMark24Filled />
            },
            {
              id: "tour",
              label: <FormattedMessage id="help.tour" defaultMessage="Tour" />,
              onClick: startTour,
              icon: <FlagFilled />
            },
            {
              id: "reportBug",
              label: <FormattedMessage id="help.report-bug" defaultMessage="Report a Bug" />,
              onClick: handleReportBugOpen,
              icon: <BugFilled />
            }
          ]
        }
      ].filter(Boolean)
    }
  ];
  const visibleMenuItems = filterToolbarMoreMenuGroups(
    filterParticipantLayoutActiveMenuGroups(menuItems, participantLayoutActive)
  );

  return (
    <>
      <ToolbarMoreButton styles={styles} items={visibleMenuItems} />
      <SummaryGenerationDialog
        open={summaryDialogOpen}
        onOpenChange={(_, data) => setSummaryDialogOpen(data.open)}
        onGenerate={onGenerateSummaryFromDialog}
        isGenerating={isGeneratingSummary}
        summaryDataSources={summaryDataSources}
      />
      {/* TODO: Move this dialog to a root level component */}
      <ReportBugDialog open={reportBugOpen} onConfirm={handleReportBugConfirm} onClose={handleReportBugClose} />
    </>
  );
}

export default FluentToolbarMoreButtonContainer;

FluentToolbarMoreButtonContainer.propTypes = {
  styles: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
  scene: PropTypes.object.isRequired,
  hubChannel: PropTypes.object.isRequired,
  hub: PropTypes.object,
  people: PropTypes.array,
  mediaSearchStore: PropTypes.object.isRequired,
  initialPresence: PropTypes.object,
  toggleStreamerMode: PropTypes.func,
  participantLayoutEnabled: PropTypes.bool,
  participantLayoutForced: PropTypes.bool,
  participantLayoutRoomEnabled: PropTypes.bool,
  participantLayoutForcedState: PropTypes.string,
  onParticipantLayoutToggle: PropTypes.func,
  onParticipantLayoutManagementCheckedValueChange: PropTypes.func,
  performConditionalSignIn: PropTypes.func
};
